import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import { Download, Search, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { sanitizeText, sanitizeFileSize, sanitizeUrl } from '../utils/sanitize';

interface CaseFile {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  encrypted: boolean;
  caseNumber: string;
}

const EngineerPortal: React.FC = () => {
  const [files, setFiles] = useState<CaseFile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/engineer/cases', {
        params: { search: searchTerm },
      });
      setFiles(response.data.files);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDownload = async (fileId: string) => {
    try {
      const response = await axios.get(`/api/v1/engineer/files/${fileId}/download`);
      // SECURITY: Validate URL protocol to prevent javascript: injection
      const safeUrl = sanitizeUrl(response.data.downloadUrl);
      window.location.href = safeUrl;
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Engineer Portal
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            label="Search cases or files"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') fetchFiles();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton onClick={fetchFiles}>
                    <Search />
                  </IconButton>
                ),
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchFiles}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Case Number</TableCell>
              <TableCell>File Name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <TableCell>{sanitizeText(file.caseNumber)}</TableCell>
                <TableCell>{sanitizeText(file.fileName)}</TableCell>
                <TableCell>{sanitizeFileSize(file.fileSize)}</TableCell>
                <TableCell>
                  {new Date(file.uploadedAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {file.encrypted && (
                    <Chip label="Encrypted" size="small" color="primary" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleDownload(file.id)}
                  >
                    <Download />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default EngineerPortal;